// ModelEditsDrawer
//
// "Show model edits" on /analytics/models: the change a push made, with
// both generations of geometry in one scene.
//
// Everything else in the suite shows a revision as counts (+12 −3 ~40)
// or as a list of names. Neither answers the question a coordinator
// actually has, which is "what does the change LOOK like" - a wall that
// moved four feet and a wall that was rebuilt in place produce the same
// row. So this loads the fragments the new push carries AND the ones the
// baseline carried for the same elements, and puts a slider between
// them: at either end one generation is solid and the other is a
// transparent shell you read it through.
//
// Data: GET /commits/:id/edit-fragments (back/routes/commits.js), which
// resolves each side's ModelVersion, reads that version's tile sidecars
// for the changed uniqueIds and hands back per-instance matrices plus
// the fragment urls out of the sidecar's own lookup. Old geometry only
// exists for pushes made after versioned exports landed - the response
// says so explicitly and the drawer repeats it rather than silently
// drawing the new shape twice.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Drawer, Slider, Checkbox, Input, Tag, Empty, Skeleton, Alert, Button, Tooltip, message } from "antd";
import { AimOutlined, ReloadOutlined, FullscreenOutlined, FullscreenExitOutlined, EyeOutlined } from "@ant-design/icons";
import moment from "moment";
import { getCommitEditFragments } from "api/commits/commitsApi";
import createEditsScene, { sideColor } from "helpers/editsScene";
import { CHANGE_LABELS } from "helpers/diffOverlay";
import { describeFragment, describePush, VERB_COLOR, VERB_LABEL } from "helpers/editNarrative";
import { renderFragmentThumbnail } from "helpers/fragmentThumbnail";
import { resolveFragmentUrl } from "helpers/fragmentUrl";
import { CHANGE_COLORS } from "helpers/diffOverlay";
import RevisionIssueRail from "components/Coordination/RevisionIssueRail";

// The four questions people ask of a revision. `moved` is split out of
// `modified` because "it changed shape" and "it is somewhere else" send a
// reviewer looking in completely different places.
const BUCKETS = ["added", "removed", "modified", "moved"];
const BUCKET_LABEL = { added: "added", removed: "deleted", modified: "edited", moved: "moved" };

function bucketOf(f) {
  if (f.change === "added") return "added";
  if (f.change === "removed") return "removed";
  const v = describeFragment(f).verb;
  return (v === "moved" || v === "rotated" || v === "movedAndRotated" || v === "relocated")
    ? "moved" : "modified";
}

const T = {
  ink: "#23343E", text: "#3A4952", muted: "#8D8D8D", line: "#EFEFEF",
  bg: "#FAFAFA", wash: "#F3F7F9",
};

const fragmentUrl = resolveFragmentUrl;

function ChangeDot({ change, side }) {
  return (
    <span style={{
      width: 9, height: 9, borderRadius: "50%", display: "inline-block",
      background: sideColor(side, change), flex: "0 0 auto",
    }} />
  );
}

function Shot({ title, src, loading, empty, tint }) {
  return (
    <div style={{ flex: "0 0 auto", width: 148 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, letterSpacing: ".06em", marginBottom: 4 }}>
        {title}
      </div>
      <div style={{
        width: 148, height: 148, borderRadius: 6, border: `1px solid ${T.line}`,
        background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", borderTop: `3px solid ${tint}`,
      }}>
        {src
          ? <img src={src} alt={title} style={{ maxWidth: "100%", maxHeight: "100%" }} />
          : (
            <div style={{ fontSize: 11, color: T.muted, textAlign: "center", padding: 10, lineHeight: 1.4 }}>
              {loading ? "rendering\u2026" : empty}
            </div>
          )}
      </div>
    </div>
  );
}

function JsonBlock({ title, data, empty }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, letterSpacing: ".06em", marginBottom: 4 }}>
        {title}
      </div>
      {data ? (
        <pre style={{
          margin: 0, background: "#20303A", color: "#D6E3EA", borderRadius: 6,
          padding: 10, fontSize: 11, lineHeight: 1.5, maxHeight: 260,
          overflow: "auto", whiteSpace: "pre", fontFamily: "SFMono-Regular, Menlo, Consolas, monospace",
        }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      ) : (
        <div style={{
          fontSize: 12, color: T.muted, background: T.wash, borderRadius: 6,
          padding: "10px 12px", border: `1px dashed ${T.line}`,
        }}>
          {empty}
        </div>
      )}
    </div>
  );
}

export default function ModelEditsDrawer({
  open, onClose, commitId, projectTitle,
  // Open straight into the fullscreen review, optionally with one of the
  // issue actions already armed - that is what the revision row's
  // "Fullscreen" and "Create issue" buttons do.
  fullscreen: fullscreenProp = false, initialAction = "",
}) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [payload, setPayload] = useState(null);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("");
  const [blend, setBlend] = useState(100);      // 0 = old, 100 = new
  const [showOld, setShowOld] = useState(true);
  const [showNew, setShowNew] = useState(true);
  const [progress, setProgress] = useState(null);
  // The unchanged model around the change, ghosted. On by default: a
  // wall that moved four feet means nothing floating in empty space -
  // it means something against the room it moved in. This is Export
  // history's "ghost unchanged" (TimepointModelPane), except that here
  // each mesh owns its material, so the ghost is real transparency
  // rather than the instanced pool's grey tint.
  const [showContext, setShowContext] = useState(true);
  const [ctxProgress, setCtxProgress] = useState(null);
  // Set when the two generations have finished loading. The context is
  // scenery and must never compete with them for the same R2.
  const [changeLoaded, setChangeLoaded] = useState(false);
  const [bucket, setBucket] = useState(null);
  const [shots, setShots] = useState({ old: null, new: null, loading: false });
  // Fullscreen is the mode the issue rail lives in: the two actions file a
  // picture of the 3D view, and a 46vh pane inside a 1180px drawer is not
  // a picture anyone would attach to an issue.
  const [fullscreen, setFullscreen] = useState(!!fullscreenProp);
  const [railAction, setRailAction] = useState(initialAction || "");

  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  // Which context list the live scene already holds - the scene is
  // rebuilt whenever drawList changes, so this is cleared with it.
  const ctxLoadedRef = useRef(null);

  // Each opening starts from what the caller asked for - the row's plain
  // "Show model edits" must not inherit the fullscreen the last one left.
  useEffect(() => {
    if (!open) return;
    setFullscreen(!!fullscreenProp);
    setRailAction(initialAction || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, commitId, fullscreenProp, initialAction]);

  useEffect(() => {
    if (!open || !commitId) return undefined;
    let cancelled = false;
    setLoading(true); setErr(""); setPayload(null); setSelected(null); setProgress(null); setCtxProgress(null);
    // contextScope "model" is the whole rest of the export, not just the
    // tiles the change happens to live in. A push that moved one duct has
    // its change in one tile of seventy - ghosting that tile alone leaves
    // 900 unchanged elements undrawn, which reads as a broken ghost
    // rather than a local one. The scene draws them instanced, so the
    // cost is a few hundred draw calls, not four thousand.
    getCommitEditFragments(commitId, { limit: 400, context: 4000, contextScope: "model" })
      // api/index.js's get() resolves the PARSED BODY, not an axios-style
      // { data } envelope - reading res.data made every push, however
      // many elements it changed, render as "This push changed no
      // elements". It also resolves ERROR bodies rather than throwing, so
      // a payload carrying `err` is a failure, not an empty diff.
      .then((res) => {
        if (cancelled) return;
        const body = res && res.data ? res.data : res;
        if (!body || body.err) { setErr(body?.err || "Could not load the model edits"); return; }
        setPayload(body);
      })
      .catch((e) => { if (!cancelled) setErr(e?.message || "Could not load the model edits"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, commitId]);

  const fragments = payload?.fragments || [];

  // Both generations, flattened into what the scene draws. A fragment
  // with no instance (no geometry on that side, or a push that predates
  // versioning) simply contributes nothing to draw and still lists.
  const drawList = useMemo(() => {
    const out = [];
    for (const f of fragments) {
      for (const side of ["old", "new"]) {
        const inst = f[side]?.instance;
        if (!inst || !inst.url || !inst.matrix) continue;
        out.push({
          uniqueId: f.uniqueId,
          // The scene filters on the PANEL's vocabulary, so a moved
          // element is 'moved' here; its COLOUR still comes from the
          // diff's own change, which is what the legend shows.
          change: bucketOf(f),
          colorChange: f.change,
          side,
          matrix: inst.matrix,
          url: fragmentUrl(inst.url, payload?.versions?.[side]?.manifestKey),
        });
      }
    }
    return out;
  }, [fragments, payload]);

  // The unchanged neighbours the endpoint hands back out of the same
  // tiles the change lives in. They carry no `change`, are never
  // coloured and never framed to - the scene draws them with the ghost
  // shader and nothing else.
  const contextList = useMemo(() => {
    const rows = payload?.context || [];
    const out = [];
    for (const c of rows) {
      if (!c || !c.url || !c.matrix) continue;
      out.push({
        uniqueId: c.uniqueId,
        matrix: c.matrix,
        url: fragmentUrl(c.url, payload?.versions?.new?.manifestKey),
      });
    }
    return out;
  }, [payload]);

  // Mount the scene once the drawer has actually rendered its canvas box.
  useEffect(() => {
    if (!open || loading || !canvasRef.current || !drawList.length) return undefined;
    let alive = true;
    // A pick on empty space CLEARS the selection: picking an element
    // ghosts everything else, and the click that undoes that has to be
    // the obvious one. (It used to ignore the miss, so once a single
    // element was isolated there was no way back to the whole change.)
    const scene = createEditsScene(canvasRef.current, { onPick: (uid) => setSelected(uid || null) });
    sceneRef.current = scene;
    scene.setBlend(blend / 100);
    scene.setSideVisible("old", showOld);
    scene.setSideVisible("new", showNew);
    ctxLoadedRef.current = null;
    setCtxProgress(null);
    setChangeLoaded(false);
    const done = () => { if (alive) setChangeLoaded(true); };
    scene.setFragments(drawList, (p) => setProgress(p)).then(done, done);
    return () => { alive = false; scene.dispose(); sceneRef.current = null; ctxLoadedRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading, drawList]);

  // After the change itself, never before it: the two generations are
  // what the drawer is about, and they must not queue behind 500 ghost
  // fragments. Unchecking hides the group; re-checking reloads nothing,
  // because the meshes are still there.
  useEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return undefined;
    scene.setSideVisible("context", showContext);
    if (!changeLoaded || !showContext || !contextList.length) return undefined;
    if (ctxLoadedRef.current === contextList) return undefined;
    ctxLoadedRef.current = contextList;
    scene.setContext(contextList, (p) => setCtxProgress(p)).catch(() => {});
    return undefined;
  }, [showContext, contextList, changeLoaded]);

  useEffect(() => { sceneRef.current && sceneRef.current.setBlend(blend / 100); }, [blend]);
  useEffect(() => { sceneRef.current && sceneRef.current.setSideVisible("old", showOld); }, [showOld]);
  useEffect(() => { sceneRef.current && sceneRef.current.setSideVisible("new", showNew); }, [showNew]);
  useEffect(() => { sceneRef.current && sceneRef.current.setChangeFilter(bucket); }, [bucket, drawList]);
  useEffect(() => { sceneRef.current && sceneRef.current.setFocus(selected); }, [selected, drawList]);

  const counts = useMemo(() => {
    const c = { added: 0, removed: 0, modified: 0, moved: 0 };
    for (const f of fragments) c[bucketOf(f)] += 1;
    return c;
  }, [fragments]);

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return fragments.filter((f) => {
      if (bucket && bucketOf(f) !== bucket) return false;
      if (!q) return true;
      return [f.category, f.family, f.typeName, f.level, f.name, f.uniqueId]
        .some((v) => String(v || "").toLowerCase().includes(q));
    });
  }, [fragments, filter, bucket]);

  const current = useMemo(
    () => fragments.find((f) => f.uniqueId === selected) || null,
    [fragments, selected]
  );

  // The rail's screenshots come from the live scene, not from a re-render
  // of the fragments: what gets filed is the blend, the bucket filter and
  // the angle the reviewer actually had on screen.
  const captureView = useCallback(() => (
    sceneRef.current ? sceneRef.current.capture("image/png") : null
  ), []);

  const focus = useCallback((uid) => {
    setSelected(uid);
    if (sceneRef.current && !sceneRef.current.focus(uid)) {
      message.info("That element has no geometry on either side to zoom to.");
    }
  }, []);

  // The way back out of an isolation. Picking a row - or a bucket tab -
  // lights that one thing and drops everything else to the ghost, which
  // is the point; what was missing was the undo. It is offered three
  // ways because a reviewer who has isolated an element is looking at
  // the 3D, not at the list: a banner over the view, a click on empty
  // space in the view, and Escape.
  const isolated = !!(selected || bucket);
  const showEverything = useCallback(() => {
    setSelected(null);
    setBucket(null);
    // And put the camera back over the whole change. Isolating an element
    // is usually done from the row's "zoom to" button, which flies right
    // up to it; lighting everything again without pulling back leaves the
    // reviewer looking at a wall from two feet away wondering where the
    // rest went.
    if (sceneRef.current) sceneRef.current.fit();
  }, []);

  useEffect(() => {
    if (!open || !isolated) return undefined;
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); showEverything(); } };
    // Capture: antd's Drawer closes itself on Escape, and clearing the
    // isolation has to win over closing the whole drawer.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, isolated, showEverything]);

  // Before/after pictures of the picked fragment, drawn from the two
  // fragment GLBs rather than captured from the canvas: a deleted element
  // is not in the current model at all, and the previous generation of a
  // modified one only exists in an older export - neither can be
  // screenshotted from a live view.
  useEffect(() => {
    let cancelled = false;
    if (!current) { setShots({ old: null, new: null, loading: false }); return undefined; }
    // Placement is optional for a PICTURE. When the generation's own
    // export is gone, the server still resolves the fragment's shape by
    // hash (verified against the bucket), and that is enough to draw it.
    const urlFor = (side) => {
      const rel = current[side]?.instance?.url || current[side]?.shape?.url;
      return rel ? fragmentUrl(rel, payload?.versions?.[side]?.manifestKey) : "";
    };
    setShots({ old: null, new: null, loading: true });
    Promise.all([
      renderFragmentThumbnail(urlFor("old"), { size: 132, color: "#9B7FD4" }),
      renderFragmentThumbnail(urlFor("new"), { size: 132, color: "#5B8DEF" }),
    ]).then(([o, n]) => { if (!cancelled) setShots({ old: o, new: n, loading: false }); });
    return () => { cancelled = true; };
  }, [current, payload]);

  const meta = payload?.meta;
  const cov = payload?.coverage;
  const pushText = useMemo(() => describePush(payload), [payload]);
  const currentStory = useMemo(() => (current ? describeFragment(current) : null), [current]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      // "100%" of the drawer root (which is already inset:0), NOT a
      // viewport width - the app renders inside html{zoom:.8} and vw
      // units do not survive it intact.
      //
      // maxWidth:none is the part that actually matters: antd caps the
      // panel at `max-width: 100vw`, and under the zoom that computes to
      // the UNZOOMED window width - 1600 of the root's 2000 - so the
      // "fullscreen" drawer sat 20% short and left the sidebar showing
      // no matter what width was asked for (2000px included).
      width={fullscreen ? "100%" : "min(1180px, calc(94 * var(--vw)))"}
      styles={fullscreen ? { wrapper: { maxWidth: "none" } } : undefined}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, color: T.ink }}>Model edits</span>
          {projectTitle ? <span style={{ fontSize: 13, color: T.muted }}>{projectTitle}</span> : null}
          {meta ? (
            <>
              <Tag style={{ margin: 0 }}>{meta.label || meta.revitDocTitle || "Model push"}</Tag>
              <span style={{ fontSize: 12, color: T.muted }}>{moment(meta.ts).format("MMM D, YYYY h:mm A")}</span>
              {meta.scope === "selection" && (
                <Tooltip title="A selection push carries only the elements the author picked, so it reports no deletions">
                  <Tag color="default" style={{ margin: 0 }}>selection</Tag>
                </Tooltip>
              )}
              {meta.reviewStatus === "pending" && <Tag color="orange" style={{ margin: 0 }}>awaiting approval</Tag>}
            </>
          ) : null}
          <span style={{ flex: 1 }} />
          <Tooltip title={fullscreen ? "Back to the drawer" : "Full screen - adds the issue actions on the right"}>
            <Button
              size="small"
              icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setFullscreen((v) => !v)}
            >
              {fullscreen ? "Exit full screen" : "Full screen"}
            </Button>
          </Tooltip>
        </div>
      }
      bodyStyle={{ padding: 0, background: T.bg }}
    >
      {/* The rail lives OUTSIDE the "has fragments" branch on purpose. A
          push that changed nothing is still a revision someone may want
          to raise an issue against, and plenty of them exist - on
          production most rows read "Nothing changed in this push". The
          drawer used to answer those with an empty screen and no way to
          act on it. */}
      <div style={{ display: "flex", height: "100%", minHeight: 0 }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", height: "100%" }}>
      {loading ? (
        <div style={{ padding: 20 }}><Skeleton active paragraph={{ rows: 8 }} /></div>
      ) : err ? (
        <div style={{ padding: 20 }}><Alert type="error" showIcon message="Could not load the model edits" description={err} /></div>
      ) : !payload || !fragments.length ? (
        <div style={{ padding: 40 }}><Empty description="This push changed no elements." /></div>
      ) : (
        <>
          {/* ── what happened, in words ──────────────────────────────
              The counts say "+1 ~3". This says "Replaced 1 door. Recut 1
              wall. Moved 1 mechanical unit and rotated another." The
              server classifies from the hashes; where both instance
              matrices are present this refines "moved or rotated" into
              the actual distance and angle. */}
          <div style={{ padding: "12px 16px", background: "#fff", borderBottom: `1px solid ${T.line}` }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, letterSpacing: ".06em", marginBottom: 4 }}>
              WHAT CHANGED
            </div>
            <div style={{ fontSize: 14.5, color: T.ink, fontWeight: 600, lineHeight: 1.5 }}>
              {pushText.text}
            </div>
            <div style={{ fontSize: 11.5, color: T.muted, marginTop: 4 }}>
              {pushText.refined
                ? "Distances and angles are measured from the two placement matrices, not inferred from hashes."
                : "Movement and rotation cannot be told apart without the earlier export's placement — both read as \u201cmoved or rotated\u201d."}
            </div>
          </div>

          {/* ── the two generations ─────────────────────────────────── */}
          <div style={{
            position: "relative",
            // In fullscreen the model IS the page: the change list keeps a
            // fixed strip at the bottom and everything else goes to the 3D.
            ...(fullscreen
              ? { flex: 1, minHeight: 320 }
              : { height: "calc(46 * var(--vh))", minHeight: 260 }),
            background: "#F4F6F7", borderBottom: `1px solid ${T.line}`,
          }}>
            <div ref={canvasRef} style={{ position: "absolute", inset: 0 }} />
            {!drawList.length && (
              <div style={{
                position: "absolute", inset: 0, display: "flex", alignItems: "center",
                justifyContent: "center", textAlign: "center", padding: 24,
              }}>
                <div style={{ maxWidth: 460, color: T.muted, fontSize: 13 }}>
                  No fragment geometry is available for this push, so there is nothing to draw.
                  The change list below is still complete.
                  {cov?.oldUnavailable ? <div style={{ marginTop: 8 }}>{cov.oldUnavailable}</div> : null}
                </div>
              </div>
            )}
            {progress && progress.loaded < progress.total ? (
              <div style={{
                position: "absolute", left: 12, top: 12, background: "rgba(255,255,255,.92)",
                borderRadius: 6, padding: "4px 10px", fontSize: 11.5, color: T.text,
              }}>
                Loading fragments {progress.loaded}/{progress.total}
                {progress.failed ? ` · ${progress.failed} unavailable` : ""}
              </div>
            ) : ctxProgress && ctxProgress.loaded < ctxProgress.total ? (
              <div style={{
                position: "absolute", left: 12, top: 12, background: "rgba(255,255,255,.92)",
                borderRadius: 6, padding: "4px 10px", fontSize: 11.5, color: T.muted,
              }}>
                Ghosting context {ctxProgress.loaded}/{ctxProgress.total}
              </div>
            ) : null}
            <div style={{
              position: "absolute", right: 12, top: 12, display: "flex", gap: 6,
            }}>
              <Tooltip title="Frame everything again">
                <Button size="small" icon={<ReloadOutlined />} onClick={() => sceneRef.current && sceneRef.current.fit()} />
              </Tooltip>
            </div>

            {/* Isolating is one click; getting back out used to be none.
                This says what is being shown and undoes it. */}
            {isolated && (
              <div style={{
                position: "absolute", left: "50%", top: 12, transform: "translateX(-50%)",
                display: "flex", alignItems: "center", gap: 10,
                background: "rgba(255,255,255,.95)", border: `1px solid ${T.line}`,
                borderRadius: 20, padding: "4px 6px 4px 14px",
                boxShadow: "0 2px 10px rgba(0,0,0,.10)", maxWidth: "min(560px, 80%)",
              }}>
                <span style={{
                  fontSize: 12, color: T.text, overflow: "hidden",
                  textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {selected
                    ? `Showing only ${[current?.category, current?.typeName || current?.family].filter(Boolean).join(" · ") || selected}`
                    : `Showing ${BUCKET_LABEL[bucket]} elements only`}
                  <span style={{ color: T.muted }}> — the rest is ghosted</span>
                </span>
                <Button size="small" type="primary" ghost icon={<EyeOutlined />} onClick={showEverything}>
                  Show everything
                </Button>
              </div>
            )}
          </div>

          {/* ── slider + the two switches ───────────────────────────── */}
          <div style={{ padding: "10px 16px", background: "#fff", borderBottom: `1px solid ${T.line}`, display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 320, flex: 1 }}>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: T.muted, letterSpacing: ".06em" }}>OLD</span>
              <Slider
                style={{ flex: 1, margin: 0 }}
                min={0} max={100} value={blend} onChange={setBlend}
                tooltip={{ formatter: (v) => (v >= 50 ? `${v}% toward the new push` : `${100 - v}% toward the old push`) }}
              />
              <span style={{ fontSize: 11.5, fontWeight: 700, color: T.muted, letterSpacing: ".06em" }}>NEW</span>
            </div>
            <Checkbox checked={showOld} onChange={(e) => setShowOld(e.target.checked)}>
              <span style={{ fontSize: 12.5 }}>Show old</span>
            </Checkbox>
            <Checkbox checked={showNew} onChange={(e) => setShowNew(e.target.checked)}>
              <span style={{ fontSize: 12.5 }}>Show new</span>
            </Checkbox>
            <Tooltip title={contextList.length
              ? `Everything this push did NOT change - ${contextList.length} element${contextList.length === 1 ? "" : "s"} out of the export, drawn as one transparent grey shell.${cov?.contextTruncated ? " Capped: the model is larger than the drawer will draw." : ""}`
              : "No unchanged geometry came back for this push - there is nothing to ghost."}>
              <Checkbox
                checked={showContext && !!contextList.length}
                disabled={!contextList.length}
                onChange={(e) => setShowContext(e.target.checked)}
              >
                <span style={{ fontSize: 12.5 }}>
                  Ghost unchanged{contextList.length ? ` (${contextList.length}${cov?.contextTruncated ? "+" : ""})` : ""}
                </span>
              </Checkbox>
            </Tooltip>
          </div>

          {/* Legend: the old side cannot reuse the diff's colours, because
              "modified" on the old side means the state before it. */}
          <div style={{ padding: "6px 16px", background: "#fff", borderBottom: `1px solid ${T.line}`, display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, color: T.text }}>
            {["added", "modified", "moved", "removed"].map((c) => (
              <span key={c} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <ChangeDot change={c} side="new" /> {CHANGE_LABELS[c] || c}
              </span>
            ))}
            <span style={{ display: "flex", alignItems: "center", gap: 5, marginLeft: 6 }}>
              <ChangeDot change="modified" side="old" /> previous state
            </span>
            {contextList.length ? (
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{
                  width: 10, height: 10, borderRadius: 2, display: "inline-block",
                  background: "#9AA3A8", opacity: 0.35, border: "1px solid #9AA3A8",
                }} />
                unchanged (ghost)
              </span>
            ) : null}
            <span style={{ color: T.muted }}>
              {cov ? `${cov.newDrawable} new · ${cov.oldDrawable} old fragment${cov.oldDrawable === 1 ? "" : "s"} drawn of ${(cov.changedTotal || cov.changed).toLocaleString()} change${(cov.changedTotal || cov.changed) === 1 ? "" : "s"}` : ""}
            </span>
          </div>

          {(cov?.oldUnavailable || cov?.truncated || cov?.newError || payload?.versions?.new?.legacy) && (
            <div style={{ padding: "8px 16px", background: "#fff", borderBottom: `1px solid ${T.line}` }}>
              <Alert
                type="info" showIcon
                message={[
                  cov?.oldUnavailable,
                  cov?.newError,
                  // Without a version row of its own, the "new" side is
                  // whatever the project streams TODAY - which is this push
                  // only if nothing has been pushed since. Say it, rather
                  // than let a later revision pass for this one.
                  payload?.versions?.new?.legacy
                    ? "This push has no versioned export of its own, so the new side shows the project's current model - accurate only if nothing has been pushed since."
                    : "",
                  // What the push changed, not what the diff kept: a
                  // SpaceX-sized first push changes over a million
                  // elements and the stored lists are cut to fit the
                  // document limit, so `changed` is short. Saying the
                  // real figure, and that the rest was not written down,
                  // beats a smaller number stated with confidence.
                  cov?.truncated || cov?.storedCut
                    ? `Showing the first ${cov.returned.toLocaleString()} of ${(cov.changedTotal || cov.changed).toLocaleString()} changed elements.${
                      cov.storedCut ? ` This push was too large to record element by element, so ${cov.changed.toLocaleString()} of them were kept - the counts above are exact.` : ""}`
                    : "",
                ].filter(Boolean).join(" ")}
              />
            </div>
          )}

          {/* ── the change list, and the JSON behind whichever is picked ── */}
          <div style={{
            display: "flex", gap: 0, minHeight: 0,
            ...(fullscreen ? { flex: "0 0 260px", height: 260 } : { flex: 1 }),
          }}>
            <div style={{ width: 340, borderRight: `1px solid ${T.line}`, background: "#fff", display: "flex", flexDirection: "column", minHeight: 0 }}>
              <div style={{ padding: 8, borderBottom: `1px solid ${T.line}` }}>
                {/* Picking a bucket lights it in the 3D and ghosts
                    everything else, so the model answers the same
                    question the list is filtered to. */}
                <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
                  {BUCKETS.map((b) => (
                    <Tag
                      key={b}
                      onClick={() => { setBucket(bucket === b ? null : b); setSelected(null); }}
                      style={{
                        cursor: "pointer", margin: 0, fontSize: 11,
                        borderColor: CHANGE_COLORS[b],
                        background: bucket === b ? CHANGE_COLORS[b] : "transparent",
                        color: bucket === b ? "#fff" : "inherit",
                      }}
                    >
                      {BUCKET_LABEL[b]} {counts[b] || 0}
                    </Tag>
                  ))}
                  {bucket && (
                    <Tag style={{ cursor: "pointer", margin: 0, fontSize: 11 }} onClick={showEverything}>
                      show all
                    </Tag>
                  )}
                </div>
                <Input.Search allowClear size="small" placeholder="Filter by category, type, level…"
                              value={filter} onChange={(e) => setFilter(e.target.value)} />
              </div>
              <div style={{ overflow: "auto", flex: 1 }}>
                {shown.map((f) => {
                  const active = f.uniqueId === selected;
                  const label = [f.category, f.typeName || f.family].filter(Boolean).join(" · ") || f.uniqueId;
                  return (
                    <div
                      key={f.uniqueId}
                      // Clicking the row that is already isolated puts
                      // everything back - the same click that turned it on.
                      onClick={() => setSelected((prev) => (prev === f.uniqueId ? null : f.uniqueId))}
                      style={{
                        padding: "7px 10px", borderBottom: `1px solid ${T.line}`, cursor: "pointer",
                        background: active ? T.wash : "#fff",
                        display: "flex", gap: 8, alignItems: "flex-start",
                      }}
                    >
                      <ChangeDot change={f.change} side="new" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, color: T.ink, fontWeight: active ? 600 : 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={label}>
                          {label}
                        </div>
                        <div style={{ fontSize: 10.5, color: T.muted }}>
                          <span style={{ color: VERB_COLOR[describeFragment(f).verb] || T.muted, fontWeight: 700 }}>
                            {VERB_LABEL[describeFragment(f).verb] || CHANGE_LABELS[f.change] || f.change}
                          </span>
                          {f.level ? ` · ${f.level}` : ""}
                          {f.old?.instance ? " · has old" : ""}
                        </div>
                      </div>
                      <Tooltip title="Zoom to this element">
                        <Button size="small" type="text" icon={<AimOutlined />}
                                onClick={(e) => { e.stopPropagation(); focus(f.uniqueId); }} />
                      </Tooltip>
                    </div>
                  );
                })}
                {!shown.length && <div style={{ padding: 20 }}><Empty description="Nothing matches that filter" /></div>}
              </div>
            </div>

            <div style={{ flex: 1, minWidth: 0, padding: 14, overflow: "auto" }}>
              {!current ? (
                <div style={{ color: T.muted, fontSize: 13, paddingTop: 8 }}>
                  Pick an element - on the left or in the 3D view - to read what the push recorded for it,
                  before and after.
                </div>
              ) : (
                <>
                  {currentStory && (
                    <div style={{
                      marginBottom: 12, padding: "10px 12px", borderRadius: 6,
                      background: T.wash, border: `1px solid ${T.line}`,
                    }}>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>{currentStory.headline}</div>
                      {currentStory.evidence.length > 0 && (
                        <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12, color: T.text, lineHeight: 1.55 }}>
                          {currentStory.evidence.map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
                      {[current.category, current.typeName || current.family].filter(Boolean).join(" · ") || current.uniqueId}
                    </div>
                    <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                      {CHANGE_LABELS[current.change] || current.change}
                      {current.placementChanged ? " · placement changed" : ""}
                      {current.level ? ` · ${current.level}` : ""}
                      {" · "}<code style={{ fontSize: 11 }}>{current.uniqueId}</code>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 14, marginBottom: 14, flexWrap: "wrap" }}>
                    <Shot
                      title="BEFORE" tint="#9B7FD4" src={shots.old} loading={shots.loading}
                      empty={current.change === "added" ? "did not exist" : "this shape is no longer in any readable export"}
                    />
                    <Shot
                      title="AFTER" tint="#5B8DEF" src={shots.new} loading={shots.loading}
                      empty={current.change === "removed" ? "deleted" : "no geometry"}
                    />
                    <div style={{ flex: 1, minWidth: 200, fontSize: 11.5, color: T.muted, lineHeight: 1.5, paddingTop: 22 }}>
                      Both pictures are drawn from the fragment itself, in its own local space,
                      so a change of shape reads directly and an element that only moved looks
                      identical in both frames. Placement is what the 3D view above compares.
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
                    <JsonBlock
                      title="BEFORE (baseline push)"
                      data={current.old?.record}
                      empty={current.change === "added"
                        ? "This element did not exist before this push."
                        : "No baseline record - the earlier push has no row for this element."}
                    />
                    <JsonBlock
                      title="AFTER (this push)"
                      data={current.new?.record}
                      empty={current.change === "removed"
                        ? "This element was deleted in this push."
                        : "No record on this push."}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 14, marginTop: 14, flexWrap: "wrap" }}>
                    <JsonBlock title="OLD INSTANCE (geometry + placement)" data={current.old?.instance}
                               empty="No old geometry for this element." />
                    <JsonBlock title="NEW INSTANCE (geometry + placement)" data={current.new?.instance}
                               empty="No new geometry for this element." />
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
        </div>
        {fullscreen && meta?.projectId && (
          <RevisionIssueRail
            projectId={meta.projectId}
            commitId={meta.commitId || commitId}
            commitLabel={meta.label || meta.revitDocTitle || ""}
            projectTitle={projectTitle}
            fragments={fragments}
            forIssues={meta.forIssues || []}
            capture={captureView}
            // Whether there is a 3D view to photograph at all - a push
            // with no drawable geometry has none, and the rail says so
            // instead of offering a button that can only fail.
            canCapture={!!drawList.length}
            // The scene has finished loading its two generations - only
            // then is a capture a picture of anything.
            ready={changeLoaded && !!drawList.length}
            action={railAction}
            onAction={setRailAction}
          />
        )}
      </div>
    </Drawer>
  );
}
